import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useI18n } from "@/contexts/I18nContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface HairQuestionnaireData {
  age: string;
  gender: string;
  hasMedicalHistory: boolean;
  medicalHistoryDetails: string;
  hasFamilyHistory: boolean;
  familyHistoryDetails: string;
  diet: string;
  sleepHours: string;
  stressLevel: string;
  hairCareRoutine: string[];
  hairCareNotes: string;
}

export interface HairQuestionnaireErrors {
  age?: string;
  gender?: string;
  medicalHistoryDetails?: string;
  familyHistoryDetails?: string;
  diet?: string;
  sleepHours?: string;
  stressLevel?: string;
  hairCareRoutine?: string;
}

interface HairQuestionnaireProps {
  data: HairQuestionnaireData;
  onChange: (data: HairQuestionnaireData) => void;
  disabled?: boolean;
  errors?: HairQuestionnaireErrors;
}

export const HairQuestionnaire = ({ data, onChange, disabled, errors }: HairQuestionnaireProps) => {
  const { t } = useI18n();
  
  const hairCareOptions = [
    { id: "shampoo", label: t('questionnaire.hair_care.shampoo') },
    { id: "conditioner", label: t('questionnaire.hair_care.conditioner') },
    { id: "heat", label: t('questionnaire.hair_care.heat') },
    { id: "coloring", label: t('questionnaire.hair_care.coloring') },
    { id: "treatments", label: t('questionnaire.hair_care.treatments') },
    { id: "supplements", label: t('questionnaire.hair_care.supplements') },
  ];

  const handleHairCareChange = (optionId: string, checked: boolean) => {
    let newRoutine = [...data.hairCareRoutine];
    
    if (checked) {
      newRoutine.push(optionId);
    } else {
      newRoutine = newRoutine.filter(id => id !== optionId);
    }
    
    onChange({ ...data, hairCareRoutine: newRoutine });
  };

  return (
    <div className="space-y-6">
      {/* Age & Gender Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('questionnaire.title')}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="age" className="text-sm font-medium">
              {t('questionnaire.age.label')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="age"
              type="number"
              min="13"
              max="99"
              placeholder={t('questionnaire.age.placeholder')}
              value={data.age}
              onChange={(e) => onChange({ ...data, age: e.target.value })}
              className={`h-11 rounded-xl ${errors?.age ? 'border-destructive' : ''}`}
              disabled={disabled}
            />
            {errors?.age && (
              <p className="text-sm text-destructive">{errors.age}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="gender" className="text-sm font-medium">
              {t('questionnaire.gender.label')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={data.gender}
              onValueChange={(value) => onChange({ ...data, gender: value })}
              disabled={disabled}
            >
              <SelectTrigger id="gender" className={`h-11 rounded-xl ${errors?.gender ? 'border-destructive' : ''}`}>
                <SelectValue placeholder={t('questionnaire.gender.label')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">{t('questionnaire.gender.male')}</SelectItem>
                <SelectItem value="female">{t('questionnaire.gender.female')}</SelectItem>
                <SelectItem value="other">{t('questionnaire.gender.other')}</SelectItem>
              </SelectContent>
            </Select>
            {errors?.gender && (
              <p className="text-sm text-destructive">{errors.gender}</p>
            )}
          </div>
        </div>
      </div>

      {/* Medical History Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('questionnaire.medical_history.label')}
        </h3>
        
        <div className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              {t('questionnaire.medical_history.label')} <span className="text-destructive">*</span>
            </Label>
            <RadioGroup
              value={data.hasMedicalHistory ? "yes" : "no"}
              onValueChange={(value) => 
                onChange({ 
                  ...data, 
                  hasMedicalHistory: value === "yes",
                  medicalHistoryDetails: value === "no" ? "" : data.medicalHistoryDetails
                })
              }
              disabled={disabled}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="medical-no" />
                <Label htmlFor="medical-no" className="cursor-pointer font-normal">{t('questionnaire.no')}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="medical-yes" />
                <Label htmlFor="medical-yes" className="cursor-pointer font-normal">{t('questionnaire.yes')}</Label>
              </div>
            </RadioGroup>
          </div>

          {data.hasMedicalHistory && (
            <div className="space-y-2 animate-fade-in">
              <Label htmlFor="medical-details" className="text-sm font-medium">
                {t('questionnaire.medical_history.label')}
              </Label>
              <Textarea
                id="medical-details"
                placeholder={t('questionnaire.medical_history.details_placeholder')}
                value={data.medicalHistoryDetails}
                onChange={(e) => onChange({ ...data, medicalHistoryDetails: e.target.value })}
                className={`rounded-xl min-h-[100px] ${errors?.medicalHistoryDetails ? 'border-destructive' : ''}`}
                disabled={disabled}
                maxLength={800}
              />
              <p className="text-xs text-muted-foreground">
                {data.medicalHistoryDetails.length}/800
              </p>
              {errors?.medicalHistoryDetails && (
                <p className="text-sm text-destructive">{errors.medicalHistoryDetails}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Family History Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('questionnaire.family_history.label')}
        </h3>
        
        <div className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              {t('questionnaire.family_history.label')} <span className="text-destructive">*</span>
            </Label>
            <RadioGroup
              value={data.hasFamilyHistory ? "yes" : "no"}
              onValueChange={(value) => 
                onChange({ 
                  ...data, 
                  hasFamilyHistory: value === "yes",
                  familyHistoryDetails: value === "no" ? "" : data.familyHistoryDetails
                })
              }
              disabled={disabled}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="family-no" />
                <Label htmlFor="family-no" className="cursor-pointer font-normal">{t('questionnaire.no')}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="family-yes" />
                <Label htmlFor="family-yes" className="cursor-pointer font-normal">{t('questionnaire.yes')}</Label>
              </div>
            </RadioGroup>
          </div>

          {data.hasFamilyHistory && (
            <div className="space-y-2 animate-fade-in">
              <Label htmlFor="family-details" className="text-sm font-medium">
                {t('questionnaire.family_history.label')}
              </Label>
              <Textarea
                id="family-details"
                placeholder={t('questionnaire.family_history.details_placeholder')}
                value={data.familyHistoryDetails}
                onChange={(e) => onChange({ ...data, familyHistoryDetails: e.target.value })}
                className={`rounded-xl min-h-[100px] ${errors?.familyHistoryDetails ? 'border-destructive' : ''}`}
                disabled={disabled}
                maxLength={800}
              />
              <p className="text-xs text-muted-foreground">
                {data.familyHistoryDetails.length}/800
              </p>
              {errors?.familyHistoryDetails && (
                <p className="text-sm text-destructive">{errors.familyHistoryDetails}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Lifestyle & Hair Care Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('questionnaire.title')}
        </h3>
        
        <div className="space-y-5">
          {/* Diet */}
          <div className="space-y-2">
            <Label htmlFor="diet" className="text-sm font-medium">
              {t('questionnaire.diet.label')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={data.diet}
              onValueChange={(value) => onChange({ ...data, diet: value })}
              disabled={disabled}
            >
              <SelectTrigger id="diet" className={`h-11 rounded-xl ${errors?.diet ? 'border-destructive' : ''}`}>
                <SelectValue placeholder={t('questionnaire.diet.label')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="balanced">{t('questionnaire.diet.balanced')}</SelectItem>
                <SelectItem value="vegetarian">{t('questionnaire.diet.vegetarian')}</SelectItem>
                <SelectItem value="vegan">{t('questionnaire.diet.vegan')}</SelectItem>
                <SelectItem value="high_protein">{t('questionnaire.diet.high_protein')}</SelectItem>
              </SelectContent>
            </Select>
            {errors?.diet && (
              <p className="text-sm text-destructive">{errors.diet}</p>
            )}
          </div>

          {/* Sleep */}
          <div className="space-y-2">
            <Label htmlFor="sleep" className="text-sm font-medium">
              {t('questionnaire.sleep.label')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="sleep"
              type="number"
              min="0"
              max="14"
              step="0.5"
              placeholder={t('questionnaire.sleep.placeholder')}
              value={data.sleepHours}
              onChange={(e) => onChange({ ...data, sleepHours: e.target.value })}
              className={`h-11 rounded-xl ${errors?.sleepHours ? 'border-destructive' : ''}`}
              disabled={disabled}
            />
            {errors?.sleepHours && (
              <p className="text-sm text-destructive">{errors.sleepHours}</p>
            )}
          </div>

          {/* Stress Level */}
          <div className="space-y-2">
            <Label htmlFor="stress" className="text-sm font-medium">
              {t('questionnaire.stress.label')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={data.stressLevel}
              onValueChange={(value) => onChange({ ...data, stressLevel: value })}
              disabled={disabled}
            >
              <SelectTrigger id="stress" className={`h-11 rounded-xl ${errors?.stressLevel ? 'border-destructive' : ''}`}>
                <SelectValue placeholder={t('questionnaire.stress.label')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">{t('questionnaire.stress.low')}</SelectItem>
                <SelectItem value="moderate">{t('questionnaire.stress.moderate')}</SelectItem>
                <SelectItem value="high">{t('questionnaire.stress.high')}</SelectItem>
              </SelectContent>
            </Select>
            {errors?.stressLevel && (
              <p className="text-sm text-destructive">{errors.stressLevel}</p>
            )}
          </div>

          {/* Hair Care Routine */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              {t('questionnaire.hair_care.label')} <span className="text-destructive">*</span>
            </Label>
            <div className="space-y-3">
              {hairCareOptions.map((option) => (
                <div key={option.id} className="flex items-start space-x-3">
                  <Checkbox
                    id={option.id}
                    checked={data.hairCareRoutine.includes(option.id)}
                    onCheckedChange={(checked) => 
                      handleHairCareChange(option.id, checked as boolean)
                    }
                    disabled={disabled}
                    className="mt-1"
                  />
                  <label
                    htmlFor={option.id}
                    className="text-sm leading-relaxed cursor-pointer select-none text-foreground"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            {errors?.hairCareRoutine && (
              <p className="text-sm text-destructive">{errors.hairCareRoutine}</p>
            )}
          </div>

          {/* Optional Notes */}
          <div className="space-y-2">
            <Label htmlFor="hair-notes" className="text-sm font-medium">
              {t('questionnaire.hair_care.notes_placeholder')}
            </Label>
            <Textarea
              id="hair-notes"
              placeholder={t('questionnaire.hair_care.notes_placeholder')}
              value={data.hairCareNotes}
              onChange={(e) => onChange({ ...data, hairCareNotes: e.target.value })}
              className="rounded-xl min-h-[80px]"
              disabled={disabled}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {data.hairCareNotes.length}/500
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
